<template>
  <ul class="stepper is-size-7">
    <router-link
      v-for="(route, rI) in filteredRoutes"
      :key="`basket-route-${rI}`"
      v-slot="{ href, navigate, isActive }"
      :to="route.to"
      custom
    >
      <li
        :class="[
          $_.get(
            route,
            'isActive',
            () => isActive
          )($router.currentRoute, isActive) && 'is-active',
          route.isDisabled && 'is-disabled'
        ]"
      >
        <a
          :href="href"
          :class="[basketData.isProcessing && 'has-cursor-wait']"
          v-on="{
            ...(route.isDisabled || basketData.isProcessing
              ? { click: $e => $e.preventDefault() }
              : { click: navigate })
          }"
        >
          <span>{{ $t(route.label) }}</span>
        </a>
      </li>
    </router-link>
  </ul>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import Context from "@/mixins/context"; // Used in menu.ts
import type { PropType } from "vue";
import type { IBasketProviderData } from "@/models/providers/baskets";
import type { IBasketRoute } from "@/models/baskets";

export default defineComponent({
  name: "BasketStepper",
  mixins: [Context],
  inject: {
    $basketData: {
      from: "basketProviderData",
      type: Function as PropType<IBasketProviderData>
    }
  },
  props: {
    basketRoutes: { type: Array as PropType<IBasketRoute[]>, required: true }
  },
  computed: {
    basketData() {
      return this.$basketData();
    },
    filteredRoutes() {
      return this.$_.map(
        this.$_.filter(this.basketRoutes, route => {
          return this.$_.get(route, "if", () => true)(this);
        }),
        route => ({
          ...route,
          to: route.to(this),
          isDisabled: this.$_.get(route, "isDisabled", () => false)(this)
        })
      );
    }
  }
});
</script>

<style lang="scss" scoped>
ul.stepper {
  display: flex;
  justify-content: center;
  align-items: center;

  li {
    a {
      font-size: $size-8;
      font-weight: $weight-semibold;
      line-height: 1;
      text-transform: uppercase;
      background-color: transparent;
      color: var(--brand-color-contrast);
      transition: background-color ease-out 0s;
      cursor: pointer;
    }
    &.is-disabled {
      opacity: 0.33;
      a {
        cursor: not-allowed;
      }
    }
  }

  @mixin before-dot() {
    content: "";
    display: block;
    font-size: 0.5rem;
    width: 1em;
    height: 1em;
    border-radius: 1em;
    background: var(--brand-color-contrast);
  }

  // Inactive item (desktop device)
  @include desktop {
    li + li {
      display: flex;
      align-items: center;
      &:before {
        @include before-dot;
        margin: 0 0.5rem;
      }
    }
  }

  // Inactive item (mobile device)
  @include touch {
    li:not(.is-active) > a {
      display: flex;
      padding: 0.25rem;
      &:before {
        @include before-dot;
      }
      & > span {
        display: none;
      }
    }
  }

  // Active item (any device)
  li.is-active a {
    display: flex;
    align-items: center;
    height: 1.5rem;
    border-radius: 0.75em;
    padding: 0 0.75em;
    background-color: var(--brand-color-contrast);
    color: var(--brand-color);
    transition-duration: 0.2s;
    @include touch {
      margin: 0 0.25rem;
    }
  }
}
</style>
